import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Image from '@plone/volto/components/theme/Image/Image';
import { caminhoDaPagina, useImagensDaPagina } from './busca';
import { fotosDaGaleria } from './escolha';
import JanelaDaFoto from './JanelaDaFoto';

export { LIMITE } from './busca';

// Fotos mostradas de início e a cada "Mostrar mais" (sem rolagem infinita).
export const LOTE = 12;
// Largura da miniatura em cada faixa da grade (4, 3 e 2 colunas): o navegador
// escolhe no srcset a menor escala que a cobre.
const TAMANHOS =
  '(min-width: 1100px) 16rem, (min-width: 901px) 25vw, (min-width: 600px) 33vw, 50vw';

export const AVISO_VAZIO =
  'Nenhuma imagem dentro desta página. Suba as fotos pela tela Conteúdo e reordene arrastando.';
// Página ainda sem endereço (o /add): as fotos só existem depois de salvar.
export const AVISO_SEM_PAGINA =
  'Salve a página primeiro; depois suba as fotos pela tela Conteúdo e reordene arrastando.';
export const AVISO_ERRO =
  'Não foi possível carregar as fotos desta página. Recarregue a página para tentar de novo.';
export const AVISO_CARREGANDO = 'Carregando as fotos desta página…';
// Modo de escolha (./escolha.js), só na edição.
export const AVISO_NENHUMA_ESCOLHIDA =
  'Nenhuma foto escolhida. Marque as fotos na barra lateral.';
export const notaDasSumidas = (n) =>
  n === 1
    ? 'Uma foto escolhida não está mais nesta página.'
    : `${n} fotos escolhidas não estão mais nesta página.`;

const plural = (n, um, varios) => (n === 1 ? um : varios);

const rotuloDaMiniatura = (n, total, descricao) =>
  `Abrir foto ${n} de ${total}${descricao ? `: ${descricao}` : ''}`;

// Decisão de escopo: o bloco não é renderizado no servidor. A busca roda no
// cliente (useEffect), então o HTML que o servidor entrega vem sem as fotos e
// elas aparecem quando a página hidrata.
const GaleriaView = ({
  data = {},
  block,
  id,
  properties,
  metadata,
  emEdicao = false,
}) => {
  // Na edição o Volto passa `block`; na leitura (RenderBlocks), `id`. O
  // identificador entra na chave da busca e no id do título: sem ele, duas
  // galerias da mesma página dividiriam os dois.
  const bloco = block || id;
  const caminho = caminhoDaPagina({ properties, metadata });
  const { chave, busca, imagens } = useImagensDaPagina({ bloco, caminho });
  // Todas as imagens, ou só as escolhidas com a legenda do bloco
  // (./escolha.js). A ordem é sempre a da busca, a da tela Conteúdo.
  const { escolhidas, modo } = data;
  const { fotos, sumidas } = useMemo(
    () => fotosDaGaleria(imagens, { modo, escolhidas }),
    [imagens, modo, escolhidas],
  );

  const [visiveis, setVisiveis] = useState(LOTE);
  const [atual, setAtual] = useState(null);
  const grade = useRef(null);
  const focarEm = useRef(null);
  const origem = useRef(null);

  useEffect(() => {
    setVisiveis(LOTE);
  }, [caminho]);

  // Outra página ou outra conta (a chave leva os dois): a janela não pode
  // reabrir sozinha quando a nova busca voltar, com a miniatura de origem já
  // desconectada.
  useEffect(() => {
    setAtual(null);
    origem.current = null;
  }, [chave]);

  // Depois de "Mostrar mais", o foco vai para a primeira foto nova: o botão
  // some quando as fotos acabam, e o foco não pode cair no vazio.
  useEffect(() => {
    if (focarEm.current === null || !grade.current) return;
    const botoes = grade.current.querySelectorAll(
      'button.ag-galeria__miniatura',
    );
    if (botoes[focarEm.current]) botoes[focarEm.current].focus();
    focarEm.current = null;
  }, [visiveis]);

  // Fechou a janela: o foco volta para a miniatura que a abriu.
  const aoFechar = useCallback(() => {
    setAtual(null);
    const alvo = origem.current;
    origem.current = null;
    if (alvo && alvo.isConnected) alvo.focus();
  }, []);

  const aviso = (texto) =>
    emEdicao ? <p className="ag-galeria__aviso">{texto}</p> : null;
  // Fotos escolhidas que saíram da página: só o editor fica sabendo.
  const nota =
    emEdicao && sumidas > 0 ? (
      <p className="ag-galeria__nota">{notaDasSumidas(sumidas)}</p>
    ) : null;
  if (caminho === null) return aviso(AVISO_SEM_PAGINA);
  if (busca?.error) return aviso(AVISO_ERRO);
  if (!busca?.loaded) return aviso(AVISO_CARREGANDO);
  if (imagens.length === 0) return aviso(AVISO_VAZIO);
  // Modo de escolha sem nenhuma escolhida entre as imagens da página.
  if (fotos.length === 0) {
    if (!emEdicao) return null;
    return (
      <>
        {aviso(AVISO_NENHUMA_ESCOLHIDA)}
        {nota}
      </>
    );
  }

  const titulo = data.title;
  const idTitulo = `ag-galeria-titulo-${bloco || 'bloco'}`;
  const total = fotos.length;
  const mostradas = fotos.slice(0, visiveis);
  const restantes = total - mostradas.length;
  const proximas = Math.min(LOTE, restantes);

  return (
    <section
      className="ag-galeria"
      aria-labelledby={titulo ? idTitulo : undefined}
      aria-label={titulo ? undefined : 'Galeria de fotos'}
    >
      {titulo && (
        <h2 id={idTitulo} className="ag-galeria__titulo">
          {titulo}
        </h2>
      )}
      <ul className="ag-galeria__grade" ref={grade}>
        {mostradas.map((foto, i) => {
          const descricao = foto.description || '';
          const imagem = (
            <Image
              item={foto}
              alt={descricao}
              loading="lazy"
              sizes={TAMANHOS}
              className="ag-galeria__img"
            />
          );
          return (
            <li key={foto['@id']} className="ag-galeria__item">
              {emEdicao ? (
                // No editor, nada de botão: o Volto transforma o Enter dentro
                // de um bloco em "bloco novo".
                <div className="ag-galeria__miniatura">{imagem}</div>
              ) : (
                <button
                  type="button"
                  className="ag-galeria__miniatura"
                  aria-label={rotuloDaMiniatura(i + 1, total, descricao)}
                  onClick={(e) => {
                    origem.current = e.currentTarget;
                    setAtual(i);
                  }}
                >
                  {imagem}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {restantes > 0 &&
        (emEdicao ? (
          <p className="ag-galeria__nota">
            {`Mais ${restantes} ${plural(
              restantes,
              'foto aparece',
              'fotos aparecem',
            )} na leitura, pelo botão “Mostrar mais”.`}
          </p>
        ) : (
          <div className="ag-galeria__mais">
            <button
              type="button"
              className="ag-galeria__botao-mais"
              onClick={() => {
                focarEm.current = mostradas.length;
                setVisiveis((v) => v + LOTE);
              }}
            >
              {`Mostrar mais ${proximas} ${plural(proximas, 'foto', 'fotos')}`}
            </button>
          </div>
        ))}
      {nota}
      {!emEdicao && (
        <JanelaDaFoto
          fotos={fotos}
          atual={atual}
          rotulo={titulo || 'Galeria de fotos'}
          aoMudar={setAtual}
          aoFechar={aoFechar}
        />
      )}
    </section>
  );
};

GaleriaView.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    modo: PropTypes.string,
    escolhidas: PropTypes.arrayOf(PropTypes.object),
  }),
  block: PropTypes.string,
  id: PropTypes.string,
  properties: PropTypes.shape({ '@id': PropTypes.string }),
  metadata: PropTypes.shape({ '@id': PropTypes.string }),
  emEdicao: PropTypes.bool,
};

export default GaleriaView;
